/**
 * Canonical list of `SubscriptionStatus` enum values, mirroring
 * `backend/prisma/schema.prisma` (`enum SubscriptionStatus { TRIALING, ACTIVE,
 * PAST_DUE, CANCELED, EXPIRED }`).
 *
 * Keep in sync with the Prisma enum. The frontend matrix test
 * (`use-subscription.test.ts` → "deriveCanWrite matrix") iterates this array,
 * so adding/removing a status without updating the policy will surface as a
 * failing test rather than a silent regression.
 */
export const SUBSCRIPTION_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'EXPIRED',
] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Subscription statuses for which the frontend allows write actions
 * (i.e. shows write CTAs, does not show the "subscription locked" banner).
 *
 * This is the SIMPLE happy-path rule shared between FE and BE health checks.
 * The backend `SubscriptionGuard` adds finer-grained logic on top (read-only
 * HTTP methods, data-export carve-outs, trial-expired-without-Stripe edge
 * case, grace period) — those nuances live there and are NOT replicated here.
 *
 * NEVER use a method-aware or per-route rule on the frontend: when in doubt
 * let the backend reject. Defaulting to "writable" is the safer UX choice
 * because the backend is the authoritative gate.
 */
export const WRITABLE_SUBSCRIPTION_STATUSES = [
  'ACTIVE',
  'TRIALING',
] as const satisfies ReadonlyArray<SubscriptionStatus>;

export type WritableSubscriptionStatus = (typeof WRITABLE_SUBSCRIPTION_STATUSES)[number];

export function isWritableSubscriptionStatus(
  status: string | null | undefined,
): status is WritableSubscriptionStatus {
  if (status === null || status === undefined) {
    return false;
  }
  return (WRITABLE_SUBSCRIPTION_STATUSES as ReadonlyArray<string>).includes(status);
}
